<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <AreaProductsBanner />

      <TheNavigation
        :trigger-id="triggerId"
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-menu-button="isShowMobileMenu = !isShowMobileMenu"
      >
        <template #right-side>
          <TheButton
            :id="isCN ? 'tracking_requestdemo' : undefined"
            :title="t('products.book-a-demo')"
            :to="`/${$i18n.locale}/marketplace/products/${$route.params.id}/request-demo`"
            class="w-[110px] tablet:w-[180px]"
          >
            <span class="text-sm">
              {{ t('products.book-a-demo') }}
            </span>
          </TheButton>
          <TheButton
            v-if="hasFreeTrialPlan"
            class="ml-2 w-[110px] tablet:ml-4 tablet:w-[180px]"
            type="freeTrial"
            :title="t('products.try-it-free')"
            :to="`/${$i18n.locale}/marketplace/pricing/${$route.params.id}#free-trial-plan`"
          >
            <span class="text-sm">
              {{ t('products.try-it-free') }}
            </span>
          </TheButton>
        </template>
      </TheNavigation>

      <AreaProductsAnnouncement v-if="isShowSectionAnnouncement" />

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('overview')">
        <AreaProductsOverview id="overview" />
      </TheScrollTrigger>

      <TheScrollTrigger
        v-if="isShowSectionCapabilities"
        :offset="offset"
        @trigger="handleTrigger('capabilities')"
      >
        <AreaProductsCapabilities id="capabilities" />
      </TheScrollTrigger>

      <TheScrollTrigger
        v-if="isShowSectionCases"
        :offset="offset"
        @trigger="handleTrigger('cases')"
      >
        <AreaProductsCases id="cases" />
      </TheScrollTrigger>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('pricing')">
        <AreaProductsPricePlanBanner id="pricing" />
      </TheScrollTrigger>

      <TheScrollTrigger
        v-if="isShowSectionLearnMore"
        :offset="offset"
        @trigger="handleTrigger('learn-more')"
      >
        <AreaProductsLearnMore id="learn-more" />
      </TheScrollTrigger>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('resources')">
        <AreaProductsResources id="resources" />
      </TheScrollTrigger>

      <TheScrollTrigger :offset="offset" @trigger="handleTrigger('next-steps')">
        <AreaProductsNextSteps id="next-steps" />
      </TheScrollTrigger>

      <NewsletterSection />

      <TheNavigationMenu
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-item-button="isShowMobileMenu = false"
      />
    </template>
  </article>
</template>

<script setup lang="ts">
  definePageMeta({
    pageTransition: false,
    middleware: ['product-redirect'],
  });

  const { t } = useI18n();
  const { isCN } = useLanguage();
  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { arrived } = useScrollArrivedBottom();

  const productsStore = useProductsStore();
  const pricingStore = usePricingStore();

  const {
    productName,
    productBanner,
    isShowMobileMenu,
    isShowSectionAnnouncement,
    isShowSectionCapabilities,
    isShowSectionCases,
    isShowSectionLearnMore,
    hasFreeTrialPlan,
  } = storeToRefs(productsStore);

  const { status: GET_PRODUCT_DETAIL_STATUS } =
    await productsStore.USE_GET_PRODUCT_DETAIL();
  const { status: GET_PRODUCT_RESOURCES_STATUS } =
    await productsStore.USE_GET_PRODUCT_RESOURCES();
  const { status: GET_PRICING_PLANS_STATUS } =
    await pricingStore.USE_GET_PRICING_PLANS();

  const triggerId = ref<string>('');

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const navigation = computed((): { id: string; name: string }[] => {
    return [
      { id: 'overview', name: t('products.section-name.overview') },
      ...(isShowSectionCapabilities.value
        ? [
            {
              id: 'capabilities',
              name: t('products.section-name.capabilities'),
            },
          ]
        : []),
      ...(isShowSectionCases.value
        ? [{ id: 'cases', name: t('products.section-name.cases') }]
        : []),
      { id: 'pricing', name: t('products.section-name.pricing') },
      ...(isShowSectionLearnMore.value
        ? [
            {
              id: 'learn-more',
              name: t('products.section-name.learn-more'),
            },
          ]
        : []),
      { id: 'resources', name: t('products.section-name.resources') },
      { id: 'next-steps', name: t('products.section-name.next-steps') },
    ];
  });

  const isLoading = computed((): boolean => {
    const result =
      GET_PRODUCT_DETAIL_STATUS.value === 'success' &&
      GET_PRODUCT_RESOURCES_STATUS.value === 'success' &&
      GET_PRICING_PLANS_STATUS.value === 'success';
    return !result;
  });

  const handleTrigger = (id: string): void => {
    if (!arrived.value) {
      triggerId.value = id;
    }
  };

  watch(arrived, (value) => {
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  watch(
    [GET_PRODUCT_DETAIL_STATUS],
    (value) => {
      if (value.includes('error')) {
        showError({
          statusCode: 404,
          data: { type: 'product-region-unavailable' },
        });
      }
    },
    { immediate: true }
  );

  watch(
    [GET_PRODUCT_RESOURCES_STATUS, GET_PRICING_PLANS_STATUS],
    (value) => {
      if (value.includes('error')) {
        showError({
          statusCode: 404,
        });
      }
    },
    { immediate: true }
  );

  useSeoMeta({
    title: () => productName.value,
    ogTitle: () => provideWebTitle(productName.value),
    twitterTitle: () => provideWebTitle(productName.value),
    description: () => productBanner.value.description,
    ogDescription: () => productBanner.value.description,
    twitterDescription: () => productBanner.value.description,
  });
</script>
